// CaseStudyDetail.jsx
import React from "react";
import { useParams, Link } from "react-router-dom";
import { CASES } from "./caseData";
import "../styles/CaseStudies.css";

/**
 * downloadFileVerbose(url, suggestedName)
 * - fetches the URL, logs status & content-type
 * - if the response appears to be PDF, converts to blob and triggers download
 * - if response is not PDF or fetch fails, shows console output and opens URL in new tab
 */
async function downloadFileVerbose(url, suggestedName = "case-study.pdf") {
  try {
    console.log("[download] attempting to fetch:", url);
    const resp = await fetch(url, { cache: "no-store" });
    console.log("[download] response status:", resp.status);
    const ctype = resp.headers.get("content-type") || "";
    console.log("[download] content-type:", ctype);

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      console.error("[download] non-OK response body (first 2000 chars):", text.slice(0, 2000));
      alert(`Failed to fetch file (status ${resp.status}). See console for details. I'll open the link in a new tab as a fallback.`);
      window.open(url, "_blank", "noopener");
      return;
    }

    // If content-type doesn't look like a PDF, print sample and fallback
    if (!ctype.toLowerCase().includes("pdf")) {
      const text = await resp.text().catch(() => "");
      console.error("[download] Response is not a PDF. Body (first 2000 chars):", text.slice(0, 2000));
      alert("Server returned non-PDF content (check console). Opening in a new tab as fallback.");
      window.open(url, "_blank", "noopener");
      return;
    }

    // Looks like a PDF; convert to blob and download
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = suggestedName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // revoke the objectURL after a short delay to ensure the browser processed it
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    console.log("[download] download triggered for:", suggestedName);   
  } catch (err) {
    console.error("[download] fetch/download error:", err);
    // fallback: open in new tab so the user can still view it
    try {
      window.open(url, "_blank", "noopener");
    } catch (e) {
      console.error("[download] fallback open failed:", e);
    }
    alert("Unable to download file directly — opened in a new tab instead (check console for details).");
  }
}

/** Basic safe filename sanitizer */
function safeFilename(name) {
  return name
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9\-_.]/g, "") // allow letters, numbers, dash, underscore, dot
    .replace(/\-+/g, "-")
    .toLowerCase();
}

export default function CaseStudyDetail() {
  const { id } = useParams();
  const item = CASES.find((c) => c.id === id);

  if (!item) {
    return (
      <div className="cs-page cs-missing">
        <div className="cs-container">
          <h2>Case study not found</h2>
          <p>We couldn't find the requested case study.</p>
          <Link to="/case-studies" className="cs-btn cs-link">Back to Case Studies</Link>
        </div>
      </div>
    );
  }

  const baseName = (item.slug || item.id || "case-study").toString();
  const suggestedFilename = `${safeFilename(baseName)}.pdf`;

  return (
    <div className="cs-page cs-detail">
      <header
        className="cs-hero cs-hero-detail"
        style={{ backgroundImage: `url(${item.hero || "https://user-gen-media-assets.s3.amazonaws.com/gemini_images/98293c3d-230c-45a6-94fd-3cce7752eacf.png"})` }}
        role="img"
        aria-label={item.title}
      >
        <div className="cs-hero-overlay" />
        <div className="cs-hero-inner">
          <Link to="/case-studies" className="cs-small-back" aria-label="Back to case studies">← Back</Link>

          <div className="cs-title-card" aria-hidden="false">
            <h1>{item.title}</h1>
          </div>
        </div>

        <svg className="cs-hero-wave" viewBox="0 0 1440 80" preserveAspectRatio="none" aria-hidden>
          <path d="M0,40 C360,120 1080,-40 1440,40 L1440,80 L0,80 Z" fill="#ffffff" opacity="0.98" />
        </svg>
      </header>

      <main className="cs-container">
        <article className="cs-article">
          <div className="cs-article-meta" style={{ marginBottom: 16 }}>
            {item.pdfUrl ? (
              <>
                <a
                  className="cs-btn cs-link"
                  href={item.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open PDF in new tab"
                >
                  View PDF
                </a>

                <button
                  type="button"
                  className="cs-btn cs-btn-primary"
                  onClick={() => downloadFileVerbose(item.pdfUrl, suggestedFilename)}
                  style={{ marginLeft: 12 }}
                  aria-label="Download PDF"
                >
                  Download PDF
                </button>
              </>
            ) : (
              <span style={{ color: "#6b7280" }}>No PDF available for this case study.</span>
            )}
          </div>

          <div className="cs-article-body" dangerouslySetInnerHTML={{ __html: item.contentHtml }} />

          <div className="cs-actions" style={{ marginTop: 24 }}>
            <Link to="/case-studies" className="cs-btn cs-link">← Back to all case studies</Link>

            {item.pdfUrl ? (
              <a
                className="cs-btn cs-btn-ghost"
                href={item.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: 12 }}
              >
                Open in new tab
              </a>
            ) : null}
          </div>
        </article>
      </main>
    </div>
  );
}